/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import { T, COST_CENTERS, SYSTEM_LISTS_DEFAULTS, isCompactViewport } from "../lib/globals";
import { SYSTEM_LISTS_LABELS } from "../lib/systemListsUtils";
import { useSystemLists } from "../hooks/useSystemLists";
import { sortAlpha, sortAlphaKeepingTodosFirst } from "../lib/sortUtils";
import { Btn, Input, Select, Toast, useToast, CCBadge } from "./ui";

function SystemListsManager() {
  const [lists, saveLists] = useSystemLists();
  const [selectedKey, setSelectedKey] = useState(null);
  const [newItem, setNewItem] = useState("");
  const [newItemQty, setNewItemQty] = useState("");
  const [newItemRef, setNewItemRef] = useState("");
  const [newItemNotes, setNewItemNotes] = useState("");
  const [newItemFlag, setNewItemFlag] = useState(false);
  const [editIdx, setEditIdx] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [editQty, setEditQty] = useState("");
  const [editRef, setEditRef] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editFlag, setEditFlag] = useState(false);
  const [toast, show] = useToast();
  const [filterModule, setFilterModule] = useState("Todos");

  const modules = React.useMemo(() => sortAlphaKeepingTodosFirst(["Todos", ...new Set(Object.values(SYSTEM_LISTS_LABELS).map(l=>l.module))]), []);
  const filteredKeys = React.useMemo(() => sortAlpha(
    Object.keys(SYSTEM_LISTS_LABELS).filter(k=> filterModule==="Todos" || SYSTEM_LISTS_LABELS[k].module===filterModule),
    (key)=>SYSTEM_LISTS_LABELS[key]?.label || key
  ), [filterModule]);
  const quickStockKeys = React.useMemo(() => sortAlpha(["STOCK_BRANDS", "CATALOG_PRODUCT_TYPES", "CATALOG_PRODUCT_CATEGORIES", "CATALOG_PRODUCT_SUBCATEGORIES", "STOCK_UNITS"].filter(key => !!SYSTEM_LISTS_LABELS[key]), (key)=>SYSTEM_LISTS_LABELS[key]?.label || key), []);

  const selectedList = React.useMemo(() => selectedKey ? (lists[selectedKey] || []) : null, [selectedKey, lists]);
  const selectedMeta = selectedKey ? SYSTEM_LISTS_LABELS[selectedKey] : null;
  const listsFocused = React.useMemo(() => {
    if (!selectedKey) return filteredKeys;
    return filteredKeys.filter(key => key === selectedKey);
  }, [filteredKeys, selectedKey]);
  React.useEffect(() => {
    if (selectedKey === "FIN_CHART_ACCOUNTS" && !String(newItemNotes || "").trim()) setNewItemNotes((Array.isArray(lists?.COST_CENTERS) && lists.COST_CENTERS[0]) || COST_CENTERS[0] || "Sede");
  }, [selectedKey, lists, newItemNotes]);
  const isStockUnitsList = selectedKey === "STOCK_UNITS";
  const isCatalogTypesList = selectedKey === "CATALOG_PRODUCT_TYPES";
  const isCatalogCategoriesList = selectedKey === "CATALOG_PRODUCT_CATEGORIES";
  const isCatalogSubcategoriesList = selectedKey === "CATALOG_PRODUCT_SUBCATEGORIES";
  const isCostCenterCodesList = selectedKey === "COST_CENTER_CODES";
  const isHarasCoatsList = selectedKey === "HARAS_COATS";
  const isHarasActivityRulesList = selectedKey === "HARAS_ACTIVITY_RULES";
  const isHarasBreedRulesList = selectedKey === "HARAS_BREED_RULES";
  const isBovineWeightRulesList = selectedKey === "BOVINE_WEIGHT_CLASS_RULES";
  const isFinChartAccountsList = selectedKey === "FIN_CHART_ACCOUNTS";
  const isPayrollChargesList = selectedKey === "PAYROLL_CHARGES";
  const isTaxRegimesList = selectedKey === "TAX_REGIMES";
  const normalizeCatalogCategoryEntry = React.useCallback((entry) => ({
    name: String(entry?.name || entry?.label || "").trim(),
    typeName: String(entry?.typeName || entry?.type || "").trim(),
  }), []);
  const normalizeCatalogSubcategoryEntry = React.useCallback((entry) => ({
    name: String(entry?.name || entry?.label || "").trim(),
    categoryName: String(entry?.categoryName || entry?.category || "").trim(),
  }), []);
  const normalizeStockUnitEntry = React.useCallback((entry) => {
    if (typeof entry === "string") return { label: entry, defaultQty: "", refUnit: "" };
    return {
      label: String(entry?.label || entry?.name || entry?.unit || "").trim(),
      defaultQty: entry?.defaultQty == null ? "" : String(entry.defaultQty),
      refUnit: String(entry?.refUnit || entry?.baseUnit || entry?.referenceUnit || "").trim(),
    };
  }, []);
  const normalizeHarasCoatEntry = React.useCallback((entry) => ({
    name: String(entry?.name || entry?.label || "").trim(),
    hex: String(entry?.hex || "#64748B").trim() || "#64748B",
  }), []);
  const normalizeHarasActivityRuleEntry = React.useCallback((entry) => ({
    name: String(entry?.name || entry?.label || "").trim(),
    minSessions: String(entry?.minSessions || "0").trim(),
    maxSessions: String(entry?.maxSessions || "0").trim(),
    suggestedPctBW: String(entry?.suggestedPctBW || entry?.pct || "").trim(),
    notes: String(entry?.notes || "").trim(),
  }), []);
  const normalizeHarasBreedRuleEntry = React.useCallback((entry) => ({
    breed: String(entry?.breed || entry?.name || "").trim(),
    pctAdjust: String(entry?.pctAdjust || entry?.adjust || "0").trim(),
    notes: String(entry?.notes || "").trim(),
  }), []);
  const normalizeBovineWeightRuleEntry = React.useCallback((entry) => ({
    label: String(entry?.label || entry?.name || "").trim(),
    minAt: String(entry?.minAt ?? entry?.min ?? "0").trim(),
    maxAt: String(entry?.maxAt ?? entry?.max ?? "999").trim(),
    color: String(entry?.color || "#22C55E").trim() || "#22C55E",
  }), []);

  const normalizeCostCenterCodeEntry = React.useCallback((entry) => ({
    name: String(entry?.name || entry?.label || entry?.costCenter || "").trim(),
    code: String(entry?.code || entry?.suffix || entry?.sigla || "").trim().toUpperCase(),
  }), []);
  const normalizeFinancialAccountEntry = React.useCallback((entry) => ({
    code: String(entry?.code || entry?.id || "").trim(),
    name: String(entry?.name || entry?.label || "").trim(),
    type: String(entry?.type || "Despesa").trim() || "Despesa",
    costCenter: String(entry?.costCenter || "Sede").trim() || "Sede",
    taxable: !!entry?.taxable,
  }), []);
  const availableCatalogTypes = React.useMemo(() => (
    Array.isArray(lists?.CATALOG_PRODUCT_TYPES) ? lists.CATALOG_PRODUCT_TYPES.map(v=>String(v||"").trim()).filter(Boolean) : []
  ), [lists]);
  const normalizedCatalogCategories = React.useMemo(() => (
    Array.isArray(lists?.CATALOG_PRODUCT_CATEGORIES) ? lists.CATALOG_PRODUCT_CATEGORIES.map(normalizeCatalogCategoryEntry).filter(item=>item.name) : []
  ), [lists, normalizeCatalogCategoryEntry]);
  const availableCostCenters = React.useMemo(() => {
    const fromLists = Array.isArray(lists?.COST_CENTERS) ? lists.COST_CENTERS.map(v=>String(v||"").trim()).filter(Boolean) : [];
    return [...new Set((fromLists.length ? fromLists : COST_CENTERS).map(v=>String(v||"").trim()).filter(Boolean))];
  }, [lists]);
  const normalizedCostCenterCodes = React.useMemo(() => (
    isCostCenterCodesList ? (selectedList || []).map(normalizeCostCenterCodeEntry).filter(item=>item.name) : []
  ), [isCostCenterCodesList, selectedList, normalizeCostCenterCodeEntry]);
  const normalizedFinancialAccounts = React.useMemo(() => (
    isFinChartAccountsList ? (selectedList || []).map(normalizeFinancialAccountEntry) : []
  ), [isFinChartAccountsList, selectedList, normalizeFinancialAccountEntry]);
  const financialTypeCodeRoot = React.useMemo(() => ({ Ativo:'1.1', Passivo:'2.1', Receita:'3.1', Despesa:'4.1' }), []);
  const buildFinancialAccountCode = React.useCallback((type, excludeIdx = null) => {
    const root = financialTypeCodeRoot[String(type || 'Despesa').trim()] || financialTypeCodeRoot.Despesa;
    const used = normalizedFinancialAccounts
      .filter((_, idx) => excludeIdx == null || idx !== excludeIdx)
      .filter(item => String(item?.type || '').trim() === String(type || '').trim())
      .map(item => {
        const match = String(item?.code || '').match(/^(\d+)\.(\d+)\.(\d+)$/);
        if (!match) return 0;
        return Number(match[3] || 0);
      });
    const next = Math.max(0, ...used) + 1;
    return `${root}.${String(next).padStart(2, '0')}`;
  }, [financialTypeCodeRoot, normalizedFinancialAccounts]);
  const newFinancialAccountCode = React.useMemo(() => (
    isFinChartAccountsList ? buildFinancialAccountCode(newItemRef || 'Despesa') : ''
  ), [isFinChartAccountsList, buildFinancialAccountCode, newItemRef]);
  const normalizePayrollChargeEntry = React.useCallback((entry) => ({
    name: String(entry?.name || entry?.label || "").trim(),
    pct: String(entry?.pct ?? entry?.percent ?? "0").trim(),
    notes: String(entry?.notes || "").trim(),
  }), []);
  const normalizeTaxRegimeEntry = React.useCallback((entry) => ({
    name: String(entry?.name || entry?.label || "").trim(),
    taxes: Array.isArray(entry?.taxes) ? entry.taxes.map(v=>String(v||"").trim()).filter(Boolean) : String(entry?.taxes || entry?.tax || "").split(",").map(v=>v.trim()).filter(Boolean),
    notes: String(entry?.notes || "").trim(),
    active: entry?.active !== false,
  }), []);

  function addItem() {
    if (isCatalogTypesList) {
      const name = String(newItem || "").trim();
      if (!name) { show("Informe o tipo.","error"); return; }
      const current = Array.isArray(selectedList) ? selectedList.map(v=>String(v||"").trim()).filter(Boolean) : [];
      if (current.some(v=>v.toLowerCase()===name.toLowerCase())) { show("Tipo já existe.","error"); return; }
      saveLists({ ...lists, [selectedKey]: [...current, name] });
      setNewItem("");
      show("✅ Tipo adicionado!");
      return;
    }
    if (isCatalogCategoriesList) {
      const name = String(newItem || "").trim();
      const typeName = String(newItemQty || "").trim();
      if (!name) { show("Informe a categoria.","error"); return; }
      if (!typeName) { show("Selecione o tipo.","error"); return; }
      if (normalizedCatalogCategories.some(item => item.name.toLowerCase()===name.toLowerCase() && item.typeName.toLowerCase()===typeName.toLowerCase())) { show("Categoria já existe para esse tipo.","error"); return; }
      saveLists({ ...lists, [selectedKey]: [...normalizedCatalogCategories, { name, typeName }] });
      setNewItem(""); setNewItemQty("");
      show("✅ Categoria adicionada!");
      return;
    }
    if (isCatalogSubcategoriesList) {
      const name = String(newItem || "").trim();
      const categoryName = String(newItemQty || "").trim();
      const current = (selectedList || []).map(normalizeCatalogSubcategoryEntry);
      if (!name) { show("Informe a subcategoria.","error"); return; }
      if (!categoryName) { show("Selecione a categoria.","error"); return; }
      if (current.some(item => item.name.toLowerCase()===name.toLowerCase() && item.categoryName.toLowerCase()===categoryName.toLowerCase())) { show("Subcategoria já existe para essa categoria.","error"); return; }
      saveLists({ ...lists, [selectedKey]: [...current, { name, categoryName }] });
      setNewItem(""); setNewItemQty("");
      show("✅ Subcategoria adicionada!");
      return;
    }
    if (isCostCenterCodesList) {
      const name = String(newItem || "").trim();
      const code = String(newItemQty || "").trim().toUpperCase();
      if (!name) { show("Selecione o centro de custo.","error"); return; }
      if (!code) { show("Informe a sigla.","error"); return; }
      const normalized = normalizedCostCenterCodes;
      if (normalized.some(item => String(item.name||"").toLowerCase() === name.toLowerCase())) { show("Centro já possui sigla cadastrada.","error"); return; }
      if (normalized.some(item => String(item.code||"").toLowerCase() === code.toLowerCase())) { show("Sigla já está em uso.","error"); return; }
      const updated = {...lists, [selectedKey]: [...normalized, { name, code }]};
      saveLists(updated); setNewItem(""); setNewItemQty(""); show("✅ Sigla adicionada!");
      return;
    }
    if (isStockUnitsList) {
      const label = newItem.trim();
      if (!label) { show("Informe a unidade.","error"); return; }
      const normalized = (selectedList || []).map(normalizeStockUnitEntry);
      if (normalized.some(item => String(item.label||"").toLowerCase() === label.toLowerCase())) { show("Unidade já existe.","error"); return; }
      const updated = {...lists, [selectedKey]: [...normalized, { label, defaultQty: String(newItemQty||"").trim(), refUnit: String(newItemRef||"").trim() }]};
      saveLists(updated); setNewItem(""); setNewItemQty(""); setNewItemRef(""); show("✅ Unidade adicionada!");
      return;
    }
    if (isHarasCoatsList) {
      const name = newItem.trim();
      const hex = String(newItemRef||"#64748B").trim() || "#64748B";
      if (!name) { show("Informe a pelagem.","error"); return; }
      const normalized = (selectedList || []).map(normalizeHarasCoatEntry);
      if (normalized.some(item => String(item.name||"").toLowerCase() === name.toLowerCase())) { show("Pelagem já existe.","error"); return; }
      const updated = {...lists, [selectedKey]: [...normalized, { name, hex }]};
      saveLists(updated); setNewItem(""); setNewItemRef("#64748B"); show("✅ Pelagem adicionada!");
      return;
    }
    if (isHarasActivityRulesList) {
      const name = newItem.trim();
      if (!name) { show("Informe o nível de atividade.","error"); return; }
      const normalized = (selectedList || []).map(normalizeHarasActivityRuleEntry);
      const updated = {...lists, [selectedKey]: [...normalized, { name, minSessions:String(newItemQty||"0").trim(), maxSessions:String(newItemRef||"0").trim(), suggestedPctBW:"0.50", notes:String(newItemNotes||"").trim() }]};
      saveLists(updated); setNewItem(""); setNewItemQty(""); setNewItemRef(""); setNewItemNotes(""); setEditRef(""); show("✅ Regra adicionada!");
      return;
    }
    if (isHarasBreedRulesList) {
      const breed = newItem.trim();
      if (!breed) { show("Informe a raça.","error"); return; }
      const normalized = (selectedList || []).map(normalizeHarasBreedRuleEntry);
      const updated = {...lists, [selectedKey]: [...normalized, { breed, pctAdjust:String(newItemQty||"0").trim(), notes:String(newItemNotes||"").trim() }]};
      saveLists(updated); setNewItem(""); setNewItemQty(""); setNewItemRef(""); setNewItemNotes(""); show("✅ Regra adicionada!");
      return;
    }
    if (isBovineWeightRulesList) {
      const label = newItem.trim();
      if (!label) { show("Informe a faixa.","error"); return; }
      const normalized = (selectedList || []).map(normalizeBovineWeightRuleEntry);
      const updated = {...lists, [selectedKey]: [...normalized, { label, minAt:String(newItemQty||"0").trim(), maxAt:String(newItemRef||"999").trim(), color:String(newItemNotes||"#22C55E").trim() || "#22C55E" }]};
      saveLists(updated); setNewItem(""); setNewItemQty(""); setNewItemRef(""); setNewItemNotes(""); show("✅ Faixa adicionada!");
      return;
    }
    if (isFinChartAccountsList) {
      const type = String(newItemRef||"Despesa").trim() || "Despesa";
      const code = buildFinancialAccountCode(type);
      const name = String(newItemQty||"").trim();
      const costCenter = String(newItemNotes || availableCostCenters[0] || "Sede").trim() || (availableCostCenters[0] || "Sede");
      if (!name) { show("Informe o nome da conta.","error"); return; }
      const normalized = normalizedFinancialAccounts;
      const updated = {...lists, [selectedKey]: [...normalized, { code, name, type, costCenter, taxable:!!newItemFlag }]};
      saveLists(updated); setNewItem(""); setNewItemQty(""); setNewItemRef("Despesa"); setNewItemNotes(availableCostCenters[0] || "Sede"); setNewItemFlag(false); show(`✅ Conta adicionada com código ${code}!`);
      return;
    }
    if (isPayrollChargesList) {
      const name = newItem.trim();
      if (!name) { show("Informe o encargo.","error"); return; }
      const normalized = (selectedList || []).map(normalizePayrollChargeEntry);
      const updated = {...lists, [selectedKey]: [...normalized, { name, pct:String(newItemQty||"0").trim(), notes:String(newItemNotes||"").trim() }]};
      saveLists(updated); setNewItem(""); setNewItemQty(""); setNewItemNotes(""); show("✅ Encargo adicionado!");
      return;
    }
    if (isTaxRegimesList) {
      const name = newItem.trim();
      if (!name) { show("Informe o regime.","error"); return; }
      const normalized = (selectedList || []).map(normalizeTaxRegimeEntry);
      const updated = {...lists, [selectedKey]: [...normalized, { name, taxes:String(newItemQty||"").split(",").map(v=>v.trim()).filter(Boolean), notes:String(newItemNotes||"").trim(), active:!!newItemFlag }]};
      saveLists(updated); setNewItem(""); setNewItemQty(""); setNewItemNotes(""); setNewItemFlag(true); show("✅ Regime adicionado!");
      return;
    }
    if(!newItem.trim()){show("Informe o item.","error");return;}
    if((selectedList||[]).includes(newItem.trim())){show("Item já existe.","error");return;}
    const updated = {...lists, [selectedKey]: [...(selectedList||[]), newItem.trim()]};
    saveLists(updated); setNewItem(""); show("✅ Item adicionado!");
  }

  function removeItem(idx) {
    const base = isCatalogTypesList ? (selectedList || []).map(v=>String(v||"").trim()).filter(Boolean) : isCatalogCategoriesList ? (selectedList || []).map(normalizeCatalogCategoryEntry) : isCatalogSubcategoriesList ? (selectedList || []).map(normalizeCatalogSubcategoryEntry) : isCostCenterCodesList ? (selectedList || []).map(normalizeCostCenterCodeEntry) : isStockUnitsList ? (selectedList || []).map(normalizeStockUnitEntry) : isHarasCoatsList ? (selectedList || []).map(normalizeHarasCoatEntry) : isHarasActivityRulesList ? (selectedList || []).map(normalizeHarasActivityRuleEntry) : isHarasBreedRulesList ? (selectedList || []).map(normalizeHarasBreedRuleEntry) : isBovineWeightRulesList ? (selectedList || []).map(normalizeBovineWeightRuleEntry) : isFinChartAccountsList ? (selectedList || []).map(normalizeFinancialAccountEntry) : isPayrollChargesList ? (selectedList || []).map(normalizePayrollChargeEntry) : isTaxRegimesList ? (selectedList || []).map(normalizeTaxRegimeEntry) : (selectedList || []);
    const updated = {...lists, [selectedKey]: base.filter((_,i)=>i!==idx)};
    saveLists(updated); show("🗑️ Removido.");
  }

  function startEdit(idx) {
    if (isCatalogTypesList) {
      setEditIdx(idx); setEditVal(String((selectedList || [])[idx] || "")); setEditQty(""); setEditRef("");
      return;
    }
    if (isCatalogCategoriesList) {
      const item = normalizeCatalogCategoryEntry((selectedList || [])[idx]);
      setEditIdx(idx); setEditVal(item.name || ""); setEditQty(item.typeName || ""); setEditRef("");
      return;
    }
    if (isCatalogSubcategoriesList) {
      const item = normalizeCatalogSubcategoryEntry((selectedList || [])[idx]);
      setEditIdx(idx); setEditVal(item.name || ""); setEditQty(item.categoryName || ""); setEditRef("");
      return;
    }
    if (isCostCenterCodesList) {
      const item = normalizeCostCenterCodeEntry((selectedList || [])[idx]);
      setEditIdx(idx); setEditVal(item.name || ""); setEditQty(item.code || ""); setEditRef("");
      return;
    }
    if (isStockUnitsList) {
      const item = normalizeStockUnitEntry((selectedList || [])[idx]);
      setEditIdx(idx); setEditVal(item.label || ""); setEditQty(item.defaultQty || ""); setEditRef(item.refUnit || "");
      return;
    }
    if (isHarasCoatsList) {
      const item = normalizeHarasCoatEntry((selectedList || [])[idx]);
      setEditIdx(idx); setEditVal(item.name || ""); setEditQty(""); setEditRef(item.hex || "#64748B"); setEditNotes("");
      return;
    }
    if (isHarasActivityRulesList) {
      const item = normalizeHarasActivityRuleEntry((selectedList || [])[idx]);
      setEditIdx(idx); setEditVal(item.name || ""); setEditQty(item.minSessions || ""); setEditRef(item.maxSessions || ""); setEditNotes(item.notes || ""); setNewItemRef(item.suggestedPctBW || "");
      return;
    }
    if (isHarasBreedRulesList) {
      const item = normalizeHarasBreedRuleEntry((selectedList || [])[idx]);
      setEditIdx(idx); setEditVal(item.breed || ""); setEditQty(item.pctAdjust || ""); setEditRef(""); setEditNotes(item.notes || "");
      return;
    }
    if (isBovineWeightRulesList) {
      const item = normalizeBovineWeightRuleEntry((selectedList || [])[idx]);
      setEditIdx(idx); setEditVal(item.label || ""); setEditQty(item.minAt || ""); setEditRef(item.maxAt || "999"); setEditNotes(item.color || "#22C55E");
      return;
    }
    if (isFinChartAccountsList) {
      const item = normalizeFinancialAccountEntry((selectedList || [])[idx]);
      setEditIdx(idx); setEditVal(item.code || ""); setEditQty(item.name || ""); setEditRef(item.type || "Despesa"); setEditNotes(item.costCenter || availableCostCenters[0] || "Sede"); setEditFlag(!!item.taxable);
      return;
    }
    if (isPayrollChargesList) {
      const item = normalizePayrollChargeEntry((selectedList || [])[idx]);
      setEditIdx(idx); setEditVal(item.name || ""); setEditQty(item.pct || "0"); setEditRef(""); setEditNotes(item.notes || "");
      return;
    }
    if (isTaxRegimesList) {
      const item = normalizeTaxRegimeEntry((selectedList || [])[idx]);
      setEditIdx(idx); setEditVal(item.name || ""); setEditQty((item.taxes || []).join(", ")); setEditRef(""); setEditNotes(item.notes || ""); setEditFlag(item.active !== false);
      return;
    }
    setEditIdx(idx); setEditVal((selectedList || [])[idx]);
  }

  function saveEdit() {
    if (isCatalogTypesList) {
      const name = String(editVal || "").trim();
      const arr = (selectedList || []).map(v=>String(v||"").trim()).filter(Boolean);
      if (!name) { show("Tipo não pode ser vazio.","error"); return; }
      if (arr.some((v,i)=>i!==editIdx && v.toLowerCase()===name.toLowerCase())) { show("Tipo já existe.","error"); return; }
      saveLists({ ...lists, [selectedKey]: arr.map((v,i)=>i===editIdx?name:v) });
      setEditIdx(null); setEditVal(""); setEditQty("");
      show("✅ Editado!");
      return;
    }
    if (isCatalogCategoriesList) {
      const name = String(editVal || "").trim();
      const typeName = String(editQty || "").trim();
      const arr = (selectedList || []).map(normalizeCatalogCategoryEntry);
      if (!name) { show("Categoria não pode ser vazia.","error"); return; }
      if (!typeName) { show("Selecione o tipo.","error"); return; }
      if (arr.some((v,i)=>i!==editIdx && v.name.toLowerCase()===name.toLowerCase() && v.typeName.toLowerCase()===typeName.toLowerCase())) { show("Categoria já existe para esse tipo.","error"); return; }
      saveLists({ ...lists, [selectedKey]: arr.map((v,i)=>i===editIdx?{...v,name,typeName}:v) });
      setEditIdx(null); setEditVal(""); setEditQty("");
      show("✅ Editado!");
      return;
    }
    if (isCatalogSubcategoriesList) {
      const name = String(editVal || "").trim();
      const categoryName = String(editQty || "").trim();
      const arr = (selectedList || []).map(normalizeCatalogSubcategoryEntry);
      if (!name) { show("Subcategoria não pode ser vazia.","error"); return; }
      if (!categoryName) { show("Selecione a categoria.","error"); return; }
      if (arr.some((v,i)=>i!==editIdx && v.name.toLowerCase()===name.toLowerCase() && v.categoryName.toLowerCase()===categoryName.toLowerCase())) { show("Subcategoria já existe para essa categoria.","error"); return; }
      saveLists({ ...lists, [selectedKey]: arr.map((v,i)=>i===editIdx?{...v,name,categoryName}:v) });
      setEditIdx(null); setEditVal(""); setEditQty("");
      show("✅ Editado!");
      return;
    }
    if (isCostCenterCodesList) {
      const name = editVal.trim();
      const code = String(editQty || "").trim().toUpperCase();
      if (!name) { show("Centro de custo é obrigatório.","error"); return; }
      if (!code) { show("Sigla é obrigatória.","error"); return; }
      const arr = (selectedList || []).map(normalizeCostCenterCodeEntry);
      if (arr.some((v,i)=>i!==editIdx && String(v.name||"").toLowerCase()===name.toLowerCase())) { show("Centro já possui sigla cadastrada.","error"); return; }
      if (arr.some((v,i)=>i!==editIdx && String(v.code||"").toLowerCase()===code.toLowerCase())) { show("Sigla já está em uso.","error"); return; }
      const updated = {...lists, [selectedKey]: arr.map((v,i)=>i===editIdx?{...v,name,code}:v)};
      saveLists(updated); setEditIdx(null); setEditVal(""); setEditQty(""); setEditRef(""); show("✅ Editado!");
      return;
    }
    if (isStockUnitsList) {
      const label = editVal.trim();
      if (!label) { show("Unidade não pode ser vazia.","error"); return; }
      const arr = (selectedList || []).map(normalizeStockUnitEntry);
      if (arr.some((v,i)=>i!==editIdx && String(v.label||"").toLowerCase()===label.toLowerCase())) { show("Unidade já existe.","error"); return; }
      const updated = {...lists, [selectedKey]: arr.map((v,i)=>i===editIdx?{...v,label,defaultQty:String(editQty||"").trim(),refUnit:String(editRef||"").trim()}:v)};
      saveLists(updated); setEditIdx(null); setEditVal(""); setEditQty(""); setEditRef(""); show("✅ Editado!");
      return;
    }
    if (isHarasCoatsList) {
      const name = editVal.trim();
      const hex = String(editRef||"#64748B").trim() || "#64748B";
      if (!name) { show("Pelagem não pode ser vazia.","error"); return; }
      const arr = (selectedList || []).map(normalizeHarasCoatEntry);
      if (arr.some((v,i)=>i!==editIdx && String(v.name||"").toLowerCase()===name.toLowerCase())) { show("Pelagem já existe.","error"); return; }
      const updated = {...lists, [selectedKey]: arr.map((v,i)=>i===editIdx?{...v,name,hex}:v)};
      saveLists(updated); setEditIdx(null); setEditVal(""); setEditQty(""); setEditRef("#64748B"); setEditNotes(""); show("✅ Editado!");
      return;
    }
    if (isHarasActivityRulesList) {
      const name = editVal.trim();
      if (!name) { show("Nível não pode ser vazio.","error"); return; }
      const arr = (selectedList || []).map(normalizeHarasActivityRuleEntry);
      const updated = {...lists, [selectedKey]: arr.map((v,i)=>i===editIdx?{...v,name,minSessions:String(editQty||"0").trim(),maxSessions:String(editRef||"0").trim(),suggestedPctBW:v.suggestedPctBW,notes:String(editNotes||"").trim()}:v)};
      saveLists(updated); setEditIdx(null); setEditVal(""); setEditQty(""); setEditRef(""); setEditNotes(""); show("✅ Editado!");
      return;
    }
    if (isHarasBreedRulesList) {
      const breed = editVal.trim();
      if (!breed) { show("Raça não pode ser vazia.","error"); return; }
      const arr = (selectedList || []).map(normalizeHarasBreedRuleEntry);
      const updated = {...lists, [selectedKey]: arr.map((v,i)=>i===editIdx?{...v,breed,pctAdjust:String(editQty||"0").trim(),notes:String(editNotes||"").trim()}:v)};
      saveLists(updated); setEditIdx(null); setEditVal(""); setEditQty(""); setEditRef(""); setEditNotes(""); show("✅ Editado!");
      return;
    }
    if (isBovineWeightRulesList) {
      const label = editVal.trim();
      if (!label) { show("Faixa não pode ser vazia.","error"); return; }
      const arr = (selectedList || []).map(normalizeBovineWeightRuleEntry);
      const updated = {...lists, [selectedKey]: arr.map((v,i)=>i===editIdx?{...v,label,minAt:String(editQty||"0").trim(),maxAt:String(editRef||"999").trim(),color:String(editNotes||"#22C55E").trim() || "#22C55E"}:v)};
      saveLists(updated); setEditIdx(null); setEditVal(""); setEditQty(""); setEditRef(""); setEditNotes(""); show("✅ Editado!");
      return;
    }
    if (isFinChartAccountsList) {
      const name = String(editQty||"").trim();
      const type = String(editRef||"Despesa").trim() || "Despesa";
      const current = normalizeFinancialAccountEntry((selectedList || [])[editIdx] || {});
      const typeChanged = String(current?.type || "").trim() !== type;
      const code = typeChanged ? buildFinancialAccountCode(type, editIdx) : (current.code || buildFinancialAccountCode(type, editIdx));
      if (!name) { show("Nome é obrigatório.","error"); return; }
      const arr = (selectedList || []).map(normalizeFinancialAccountEntry);
      const updated = {...lists, [selectedKey]: arr.map((v,i)=>i===editIdx?{...v,code,name,type,costCenter:String(editNotes||availableCostCenters[0]||"Sede").trim() || (availableCostCenters[0]||"Sede"),taxable:!!editFlag}:v)};
      saveLists(updated); setEditIdx(null); setEditVal(""); setEditQty(""); setEditRef(""); setEditNotes(""); setEditFlag(false); show(typeChanged ? `✅ Editado! Novo código: ${code}` : "✅ Editado!");
      return;
    }
    if (isPayrollChargesList) {
      const name = editVal.trim();
      if (!name) { show("Encargo não pode ser vazio.","error"); return; }
      const arr = (selectedList || []).map(normalizePayrollChargeEntry);
      const updated = {...lists, [selectedKey]: arr.map((v,i)=>i===editIdx?{...v,name,pct:String(editQty||"0").trim(),notes:String(editNotes||"").trim()}:v)};
      saveLists(updated); setEditIdx(null); setEditVal(""); setEditQty(""); setEditRef(""); setEditNotes(""); show("✅ Editado!");
      return;
    }
    if (isTaxRegimesList) {
      const name = editVal.trim();
      if (!name) { show("Regime não pode ser vazio.","error"); return; }
      const arr = (selectedList || []).map(normalizeTaxRegimeEntry);
      const updated = {...lists, [selectedKey]: arr.map((v,i)=>i===editIdx?{...v,name,taxes:String(editQty||"").split(",").map(t=>t.trim()).filter(Boolean),notes:String(editNotes||"").trim(),active:!!editFlag}:v)};
      saveLists(updated); setEditIdx(null); setEditVal(""); setEditQty(""); setEditRef(""); setEditNotes(""); setEditFlag(false); show("✅ Editado!");
      return;
    }
    if(!editVal.trim()){show("Item não pode ser vazio.","error");return;}
    const updated = {...lists, [selectedKey]: (selectedList||[]).map((v,i)=>i===editIdx?editVal.trim():v)};
    saveLists(updated); setEditIdx(null); setEditVal(""); show("✅ Editado!");
  }

  function moveItem(idx, dir) {
    const arr = [...selectedList];
    const to = idx + dir;
    if(to<0||to>=arr.length) return;
    [arr[idx],arr[to]] = [arr[to],arr[idx]];
    saveLists({...lists,[selectedKey]:arr});
  }

  const compactListsLayout = isCompactViewport();
  const financeAdminListLayout = compactListsLayout && (isFinChartAccountsList || isPayrollChargesList || isTaxRegimesList);
  const bovineWeightRulesMobileLayout = compactListsLayout && isBovineWeightRulesList;
  const listEditor = selectedKey&&selectedMeta&&(
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:compactListsLayout?14:20,marginTop:compactListsLayout?8:0}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
              <div>
                <div style={{fontWeight:700,color:T.text,fontSize:15}}>
                  {selectedMeta.icon} {selectedMeta.label}
                </div>
                <div style={{fontSize:11,color:T.muted,marginTop:2}}>{selectedMeta.module}</div>
              </div>
              <Btn size="sm" variant="ghost" onClick={()=>setSelectedKey("")}>Fechar</Btn>
            </div>

            {/* Add new item */}
            {isCostCenterCodesList ? (
              <div style={{display:"grid",gridTemplateColumns:compactListsLayout?"1fr":"minmax(180px,1fr) minmax(120px,.6fr) auto",gap:8,marginBottom:16}}>
                <Select value={newItem} onChange={e=>setNewItem(e.target.value)}>
                  <option value="">Centro de custo...</option>
                  {availableCostCenters.map(cc=><option key={cc} value={cc}>{cc}</option>)}
                </Select>
                <Input value={newItemQty} onChange={e=>setNewItemQty(String(e.target.value||'').toUpperCase())} placeholder="Sigla" maxLength={4} onKeyDown={e=>e.key==="Enter"&&addItem()}/>
                <Btn onClick={addItem}>+ Adicionar</Btn>
              </div>
            ) : isCatalogTypesList ? (
              <div style={{display:"grid",gridTemplateColumns:"minmax(220px,1fr) auto",gap:8,marginBottom:16}}>
                <Input value={newItem} onChange={e=>setNewItem(e.target.value)} placeholder="Tipo do catálogo..." onKeyDown={e=>e.key==="Enter"&&addItem()}/>
                <Btn onClick={addItem}>+ Adicionar</Btn>
              </div>
            ) : isCatalogCategoriesList ? (
              <div style={{display:"grid",gridTemplateColumns:compactListsLayout?"1fr":"minmax(200px,1fr) minmax(220px,1fr) auto",gap:8,marginBottom:16}}>
                <Input value={newItem} onChange={e=>setNewItem(e.target.value)} placeholder="Categoria..." onKeyDown={e=>e.key==="Enter"&&addItem()}/>
                <Select value={newItemQty||""} onChange={e=>setNewItemQty(e.target.value)}><option value="">Selecione o tipo...</option>{availableCatalogTypes.map(opt=><option key={opt} value={opt}>{opt}</option>)}</Select>
                <Btn onClick={addItem}>+ Adicionar</Btn>
              </div>
            ) : isCatalogSubcategoriesList ? (
              <div style={{display:"grid",gridTemplateColumns:compactListsLayout?"1fr":"minmax(200px,1fr) minmax(220px,1fr) auto",gap:8,marginBottom:16}}>
                <Input value={newItem} onChange={e=>setNewItem(e.target.value)} placeholder="Subcategoria..." onKeyDown={e=>e.key==="Enter"&&addItem()}/>
                <Select value={newItemQty||""} onChange={e=>setNewItemQty(e.target.value)}><option value="">Selecione a categoria...</option>{normalizedCatalogCategories.map(opt=><option key={`${opt.typeName}:${opt.name}`} value={opt.name}>{opt.typeName} → {opt.name}</option>)}</Select>
                <Btn onClick={addItem}>+ Adicionar</Btn>
              </div>
            ) : isStockUnitsList ? (
              <div style={{display:"grid",gridTemplateColumns:"minmax(180px,1.2fr) minmax(120px,.7fr) minmax(120px,.7fr) auto",gap:8,marginBottom:16}}>
                <Input value={newItem} onChange={e=>setNewItem(e.target.value)} placeholder="Unidade..." onKeyDown={e=>e.key==="Enter"&&addItem()}/>
                <Input value={newItemQty} onChange={e=>setNewItemQty(e.target.value)} placeholder="Qtd padrão" onKeyDown={e=>e.key==="Enter"&&addItem()}/>
                <Input value={newItemRef} onChange={e=>setNewItemRef(e.target.value)} placeholder="Ref. ex.: L, kg" onKeyDown={e=>e.key==="Enter"&&addItem()}/>
                <Btn onClick={addItem} style={bovineWeightRulesMobileLayout && isBovineWeightRulesList ? {gridColumn:"1 / -1", width:"100%"} : undefined}>+ Adicionar</Btn>
              </div>
            ) : isHarasCoatsList ? (
              <div style={{display:"grid",gridTemplateColumns:"minmax(180px,1.2fr) minmax(120px,.7fr) auto",gap:8,marginBottom:16}}>
                <Input value={newItem} onChange={e=>setNewItem(e.target.value)} placeholder="Pelagem..." onKeyDown={e=>e.key==="Enter"&&addItem()}/>
                <Input type="color" value={newItemRef||"#64748B"} onChange={e=>setNewItemRef(e.target.value)} style={{minHeight:42,padding:4}}/>
                <Btn onClick={addItem}>+ Adicionar</Btn>
              </div>
            ) : isBovineWeightRulesList ? (
              <div style={{display:"grid",gridTemplateColumns:bovineWeightRulesMobileLayout?"1fr 1fr":"minmax(180px,1.2fr) minmax(90px,.6fr) minmax(90px,.6fr) minmax(110px,.7fr) auto",gap:8,marginBottom:16,alignItems:"center"}}>
                <Input value={newItem} onChange={e=>setNewItem(e.target.value)} placeholder="Faixa..." style={{gridColumn:bovineWeightRulesMobileLayout?"1 / -1":undefined}} onKeyDown={e=>e.key==="Enter"&&addItem()}/>
                <Input value={newItemQty} onChange={e=>setNewItemQty(e.target.value)} placeholder="@ mín." onKeyDown={e=>e.key==="Enter"&&addItem()}/>
                <Input value={newItemRef} onChange={e=>setNewItemRef(e.target.value)} placeholder="@ máx." onKeyDown={e=>e.key==="Enter"&&addItem()}/>
                <Input type="color" value={newItemNotes||"#22C55E"} onChange={e=>setNewItemNotes(e.target.value)} style={{minHeight:42,padding:4,width:bovineWeightRulesMobileLayout?"100%":undefined}}/>
                <Btn onClick={addItem} style={bovineWeightRulesMobileLayout ? {gridColumn:"1 / -1", width:"100%"} : undefined}>+ Adicionar</Btn>
              </div>
            ) : isFinChartAccountsList ? (
              <div style={{display:"grid",gridTemplateColumns:financeAdminListLayout?"1fr":"minmax(120px,.7fr) minmax(220px,1.4fr) minmax(120px,.7fr) minmax(160px,.9fr) auto auto",gap:8,marginBottom:16,alignItems:"center"}}>
                <Input value={newFinancialAccountCode} readOnly placeholder="Código automático" title="Código gerado automaticamente pelo tipo da conta"/>
                <Input value={newItemQty} onChange={e=>setNewItemQty(e.target.value)} placeholder="Nome da conta" onKeyDown={e=>e.key==="Enter"&&addItem()}/>
                <Select value={newItemRef||"Despesa"} onChange={e=>setNewItemRef(e.target.value)}><option>Ativo</option><option>Passivo</option><option>Receita</option><option>Despesa</option></Select>
                <Select value={newItemNotes||availableCostCenters[0]||"Sede"} onChange={e=>setNewItemNotes(e.target.value)}>{availableCostCenters.map(cc=><option key={cc} value={cc}>{cc}</option>)}</Select>
                <label style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:T.muted}}><input type="checkbox" checked={!!newItemFlag} onChange={e=>setNewItemFlag(e.target.checked)}/> Tributável</label>
                <Btn onClick={addItem}>+ Adicionar</Btn>
              </div>
            ) : isPayrollChargesList ? (
              <div style={{display:"grid",gridTemplateColumns:financeAdminListLayout?"1fr":"minmax(220px,1.3fr) minmax(120px,.6fr) minmax(220px,1fr) auto",gap:8,marginBottom:16}}>
                <Input value={newItem} onChange={e=>setNewItem(e.target.value)} placeholder="Nome do encargo" onKeyDown={e=>e.key==="Enter"&&addItem()}/>
                <Input value={newItemQty} onChange={e=>setNewItemQty(e.target.value)} placeholder="%" onKeyDown={e=>e.key==="Enter"&&addItem()}/>
                <Input value={newItemNotes} onChange={e=>setNewItemNotes(e.target.value)} placeholder="Observação" onKeyDown={e=>e.key==="Enter"&&addItem()}/>
                <Btn onClick={addItem}>+ Adicionar</Btn>
              </div>
            ) : isTaxRegimesList ? (
              <div style={{display:"grid",gridTemplateColumns:financeAdminListLayout?"1fr":"minmax(180px,.9fr) minmax(260px,1.2fr) minmax(220px,1fr) auto auto",gap:8,marginBottom:16,alignItems:"center"}}>
                <Input value={newItem} onChange={e=>setNewItem(e.target.value)} placeholder="Regime" onKeyDown={e=>e.key==="Enter"&&addItem()}/>
                <Input value={newItemQty} onChange={e=>setNewItemQty(e.target.value)} placeholder="Tributos separados por vírgula" onKeyDown={e=>e.key==="Enter"&&addItem()}/>
                <Input value={newItemNotes} onChange={e=>setNewItemNotes(e.target.value)} placeholder="Observação" onKeyDown={e=>e.key==="Enter"&&addItem()}/>
                <label style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:T.muted}}><input type="checkbox" checked={!!newItemFlag} onChange={e=>setNewItemFlag(e.target.checked)}/> Ativo</label>
                <Btn onClick={addItem}>+ Adicionar</Btn>
              </div>
            ) : (
              <div style={{display:"flex",gap:8,marginBottom:16}}>
                <Input value={newItem} onChange={e=>setNewItem(e.target.value)}
                  placeholder="Novo item..." style={{flex:1}}
                  onKeyDown={e=>e.key==="Enter"&&addItem()}/>
                <Btn onClick={addItem}>+ Adicionar</Btn>
              </div>
            )}

            {/* Items list */}
            <div style={{display:"flex",flexDirection:"column",gap:4,maxHeight:420,overflowY:"auto"}}>
              {(selectedList||[]).map((item,idx)=>{
                const catalogCategoryItem = isCatalogCategoriesList ? normalizeCatalogCategoryEntry(item) : null;
                const catalogSubcategoryItem = isCatalogSubcategoriesList ? normalizeCatalogSubcategoryEntry(item) : null;
                const stockUnitItem = isStockUnitsList ? normalizeStockUnitEntry(item) : null;
                const financialAccountItem = isFinChartAccountsList ? normalizeFinancialAccountEntry(item) : null;
                const payrollChargeItem = isPayrollChargesList ? normalizePayrollChargeEntry(item) : null;
                const taxRegimeItem = isTaxRegimesList ? normalizeTaxRegimeEntry(item) : null;
                return (
                <div key={idx} style={{display:"flex",flexDirection:bovineWeightRulesMobileLayout?"column":"row",alignItems:bovineWeightRulesMobileLayout?"stretch":(financeAdminListLayout?"flex-start":"center"),gap:8,padding:compactListsLayout?"10px 12px":"8px 12px",
                  background:T.card2,borderRadius:8,border:`1px solid ${T.border}`}}>
                  {/* Reorder buttons */}
                  <div style={{display:"flex",flexDirection:bovineWeightRulesMobileLayout?"row":"column",gap:bovineWeightRulesMobileLayout?6:1,alignSelf:bovineWeightRulesMobileLayout?"flex-end":"stretch"}}>
                    <button onClick={()=>moveItem(idx,-1)} disabled={idx===0}
                      style={{background:"none",border:"none",color:idx===0?T.border:T.muted,cursor:idx===0?"default":"pointer",fontSize:10,lineHeight:1,padding:"1px 4px"}}>▲</button>
                    <button onClick={()=>moveItem(idx,+1)} disabled={idx===((selectedList||[]).length-1)}
                      style={{background:"none",border:"none",color:idx===((selectedList||[]).length-1)?T.border:T.muted,cursor:idx===((selectedList||[]).length-1)?"default":"pointer",fontSize:10,lineHeight:1,padding:"1px 4px"}}>▼</button>
                  </div>

                  {editIdx===idx?(
                    isCatalogTypesList ? (
                      <div style={{flex:1,display:"grid",gridTemplateColumns:"1fr auto auto",gap:6}}>
                        <Input value={editVal} onChange={e=>setEditVal(e.target.value)} style={{fontSize:13}} onKeyDown={e=>e.key==="Enter"&&saveEdit()}/>
                        <Btn size="sm" onClick={saveEdit}>✓</Btn>
                        <Btn size="sm" variant="ghost" onClick={()=>{setEditIdx(null);setEditVal("");setEditQty("");}}>✕</Btn>
                      </div>
                    ) : isCatalogCategoriesList ? (
                      <div style={{flex:1,display:"grid",gridTemplateColumns:compactListsLayout?"1fr":"minmax(180px,1fr) minmax(180px,1fr) auto auto",gap:6,alignItems:"center"}}>
                        <Input value={editVal} onChange={e=>setEditVal(e.target.value)} style={{fontSize:13}} onKeyDown={e=>e.key==="Enter"&&saveEdit()}/>
                        <Select value={editQty||""} onChange={e=>setEditQty(e.target.value)}><option value="">Tipo...</option>{availableCatalogTypes.map(opt=><option key={opt} value={opt}>{opt}</option>)}</Select>
                        <Btn size="sm" onClick={saveEdit}>✓</Btn>
                        <Btn size="sm" variant="ghost" onClick={()=>{setEditIdx(null);setEditVal("");setEditQty("");}}>✕</Btn>
                      </div>
                    ) : isCatalogSubcategoriesList ? (
                      <div style={{flex:1,display:"grid",gridTemplateColumns:compactListsLayout?"1fr":"minmax(180px,1fr) minmax(180px,1fr) auto auto",gap:6,alignItems:"center"}}>
                        <Input value={editVal} onChange={e=>setEditVal(e.target.value)} style={{fontSize:13}} onKeyDown={e=>e.key==="Enter"&&saveEdit()}/>
                        <Select value={editQty||""} onChange={e=>setEditQty(e.target.value)}><option value="">Categoria...</option>{normalizedCatalogCategories.map(opt=><option key={`${opt.typeName}:${opt.name}`} value={opt.name}>{opt.typeName} → {opt.name}</option>)}</Select>
                        <Btn size="sm" onClick={saveEdit}>✓</Btn>
                        <Btn size="sm" variant="ghost" onClick={()=>{setEditIdx(null);setEditVal("");setEditQty("");}}>✕</Btn>
                      </div>
                    ) : isCostCenterCodesList ? (
                      <div style={{flex:1,display:"grid",gridTemplateColumns:compactListsLayout?"1fr":"minmax(180px,1fr) minmax(120px,.6fr) auto auto",gap:6,alignItems:"center"}}>
                        <Select value={editVal} onChange={e=>setEditVal(e.target.value)}>
                          <option value="">Centro de custo...</option>
                          {availableCostCenters.map(cc=><option key={cc} value={cc}>{cc}</option>)}
                        </Select>
                        <Input value={editQty} onChange={e=>setEditQty(String(e.target.value||'').toUpperCase())} maxLength={4} placeholder="Sigla" onKeyDown={e=>e.key==="Enter"&&saveEdit()}/>
                        <Btn size="sm" onClick={saveEdit}>✓</Btn>
                        <Btn size="sm" variant="ghost" onClick={()=>{setEditIdx(null);setEditVal("");setEditQty("");setEditRef("");}}>✕</Btn>
                      </div>
                    ) : isStockUnitsList ? (
                      <div style={{flex:1,display:"grid",gridTemplateColumns:"minmax(180px,1.2fr) minmax(120px,.7fr) minmax(120px,.7fr) auto auto",gap:6}}>
                        <Input value={editVal} onChange={e=>setEditVal(e.target.value)} style={{fontSize:13}} onKeyDown={e=>e.key==="Enter"&&saveEdit()}/>
                        <Input value={editQty} onChange={e=>setEditQty(e.target.value)} placeholder="Qtd padrão" onKeyDown={e=>e.key==="Enter"&&saveEdit()}/>
                        <Input value={editRef} onChange={e=>setEditRef(e.target.value)} placeholder="Ref." onKeyDown={e=>e.key==="Enter"&&saveEdit()}/>
                        <Btn size="sm" onClick={saveEdit}>✓</Btn>
                        <Btn size="sm" variant="ghost" onClick={()=>{setEditIdx(null);setEditVal("");setEditQty("");setEditRef("");}}>✕</Btn>
                      </div>
                    ) : isHarasCoatsList ? (
                      <div style={{flex:1,display:"grid",gridTemplateColumns:"minmax(180px,1.2fr) minmax(120px,.7fr) auto auto",gap:6}}>
                        <Input value={editVal} onChange={e=>setEditVal(e.target.value)} style={{fontSize:13}} onKeyDown={e=>e.key==="Enter"&&saveEdit()}/>
                        <Input type="color" value={editRef||"#64748B"} onChange={e=>setEditRef(e.target.value)} style={{minHeight:36,padding:4}}/>
                        <Btn size="sm" onClick={saveEdit}>✓</Btn>
                        <Btn size="sm" variant="ghost" onClick={()=>{setEditIdx(null);setEditVal("");setEditQty("");setEditRef("#64748B");}}>✕</Btn>
                      </div>
                    ) : isBovineWeightRulesList ? (
                      <div style={{flex:1,display:"grid",gridTemplateColumns:bovineWeightRulesMobileLayout?"1fr 1fr":"minmax(160px,1.1fr) minmax(80px,.55fr) minmax(80px,.55fr) minmax(100px,.6fr) auto auto",gap:6,alignItems:"center"}}>
                        <Input value={editVal} onChange={e=>setEditVal(e.target.value)} style={{fontSize:13,gridColumn:bovineWeightRulesMobileLayout?"1 / -1":undefined}} onKeyDown={e=>e.key==="Enter"&&saveEdit()}/>
                        <Input value={editQty} onChange={e=>setEditQty(e.target.value)} placeholder="@ mín." onKeyDown={e=>e.key==="Enter"&&saveEdit()}/>
                        <Input value={editRef} onChange={e=>setEditRef(e.target.value)} placeholder="@ máx." onKeyDown={e=>e.key==="Enter"&&saveEdit()}/>
                        <Input type="color" value={editNotes||"#22C55E"} onChange={e=>setEditNotes(e.target.value)} style={{minHeight:36,padding:4,width:bovineWeightRulesMobileLayout?"100%":undefined}}/>
                        <Btn size="sm" onClick={saveEdit} style={bovineWeightRulesMobileLayout ? {width:"100%"} : undefined}>✓</Btn>
                        <Btn size="sm" variant="ghost" onClick={()=>{setEditIdx(null);setEditVal("");setEditQty("");setEditRef("");setEditNotes("");}} style={bovineWeightRulesMobileLayout ? {width:"100%"} : undefined}>✕</Btn>
                      </div>
                    ) : isFinChartAccountsList ? (
                      <div style={{flex:1,display:"grid",gridTemplateColumns:financeAdminListLayout?"1fr":"minmax(120px,.7fr) minmax(210px,1.2fr) minmax(110px,.7fr) minmax(140px,.8fr) auto auto auto",gap:6,alignItems:"center"}}>
                        <Input value={(editIdx!=null && String(normalizeFinancialAccountEntry((selectedList || [])[editIdx] || {}).type || '').trim() !== String(editRef||'Despesa').trim()) ? buildFinancialAccountCode(editRef||'Despesa', editIdx) : editVal} readOnly style={{fontSize:13}} title="Código gerado automaticamente pelo tipo da conta"/>
                        <Input value={editQty} onChange={e=>setEditQty(e.target.value)} placeholder="Nome" onKeyDown={e=>e.key==="Enter"&&saveEdit()}/>
                        <Select value={editRef||"Despesa"} onChange={e=>setEditRef(e.target.value)}><option>Ativo</option><option>Passivo</option><option>Receita</option><option>Despesa</option></Select>
                        <Select value={editNotes||availableCostCenters[0]||"Sede"} onChange={e=>setEditNotes(e.target.value)}>{availableCostCenters.map(cc=><option key={cc} value={cc}>{cc}</option>)}</Select>
                        <label style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:T.muted}}><input type="checkbox" checked={!!editFlag} onChange={e=>setEditFlag(e.target.checked)}/> Trib.</label>
                        <Btn size="sm" onClick={saveEdit}>✓</Btn>
                        <Btn size="sm" variant="ghost" onClick={()=>{setEditIdx(null);setEditVal("");setEditQty("");setEditRef("");setEditNotes("");setEditFlag(false);}}>✕</Btn>
                      </div>
                    ) : isPayrollChargesList ? (
                      <div style={{flex:1,display:"grid",gridTemplateColumns:financeAdminListLayout?"1fr":"minmax(200px,1.2fr) minmax(90px,.55fr) minmax(220px,1fr) auto auto",gap:6}}>
                        <Input value={editVal} onChange={e=>setEditVal(e.target.value)} style={{fontSize:13}} onKeyDown={e=>e.key==="Enter"&&saveEdit()}/>
                        <Input value={editQty} onChange={e=>setEditQty(e.target.value)} placeholder="%" onKeyDown={e=>e.key==="Enter"&&saveEdit()}/>
                        <Input value={editNotes} onChange={e=>setEditNotes(e.target.value)} placeholder="Obs." onKeyDown={e=>e.key==="Enter"&&saveEdit()}/>
                        <Btn size="sm" onClick={saveEdit}>✓</Btn>
                        <Btn size="sm" variant="ghost" onClick={()=>{setEditIdx(null);setEditVal("");setEditQty("");setEditRef("");setEditNotes("");}}>✕</Btn>
                      </div>
                    ) : isTaxRegimesList ? (
                      <div style={{flex:1,display:"grid",gridTemplateColumns:financeAdminListLayout?"1fr":"minmax(160px,.9fr) minmax(220px,1.2fr) minmax(220px,1fr) auto auto auto",gap:6,alignItems:"center"}}>
                        <Input value={editVal} onChange={e=>setEditVal(e.target.value)} style={{fontSize:13}} onKeyDown={e=>e.key==="Enter"&&saveEdit()}/>
                        <Input value={editQty} onChange={e=>setEditQty(e.target.value)} placeholder="Tributos" onKeyDown={e=>e.key==="Enter"&&saveEdit()}/>
                        <Input value={editNotes} onChange={e=>setEditNotes(e.target.value)} placeholder="Obs." onKeyDown={e=>e.key==="Enter"&&saveEdit()}/>
                        <label style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:T.muted}}><input type="checkbox" checked={!!editFlag} onChange={e=>setEditFlag(e.target.checked)}/> Ativo</label>
                        <Btn size="sm" onClick={saveEdit}>✓</Btn>
                        <Btn size="sm" variant="ghost" onClick={()=>{setEditIdx(null);setEditVal("");setEditQty("");setEditRef("");setEditNotes("");setEditFlag(false);}}>✕</Btn>
                      </div>
                    ) : (
                      <div style={{flex:1,display:"flex",gap:6}}>
                        <Input value={editVal} onChange={e=>setEditVal(e.target.value)}
                          style={{flex:1,fontSize:13}} onKeyDown={e=>e.key==="Enter"&&saveEdit()}/>
                        <Btn size="sm" onClick={saveEdit}>✓</Btn>
                        <Btn size="sm" variant="ghost" onClick={()=>setEditIdx(null)}>✕</Btn>
                      </div>
                    )
                  ):(
                    <>
                      {isCatalogTypesList ? (
                        <span style={{flex:1,fontSize:13,color:T.text,fontWeight:600}}>{String(item||"")}</span>
                      ) : isCatalogCategoriesList ? (
                        <div style={{flex:1,display:"grid",gridTemplateColumns:compactListsLayout?"1fr":"minmax(220px,1fr) minmax(220px,1fr)",gap:8,alignItems:"center"}}>
                          <span style={{fontSize:13,color:T.text,fontWeight:600}}>{catalogCategoryItem.name}</span>
                          <span style={{fontSize:12,color:T.muted}}>Tipo: {catalogCategoryItem.typeName || "—"}</span>
                        </div>
                      ) : isCatalogSubcategoriesList ? (
                        <div style={{flex:1,display:"grid",gridTemplateColumns:compactListsLayout?"1fr":"minmax(220px,1fr) minmax(220px,1fr)",gap:8,alignItems:"center"}}>
                          <span style={{fontSize:13,color:T.text,fontWeight:600}}>{catalogSubcategoryItem.name}</span>
                          <span style={{fontSize:12,color:T.muted}}>Categoria: {catalogSubcategoryItem.categoryName || "—"}</span>
                        </div>
                      ) : isCostCenterCodesList ? (
                        <div style={{flex:1,display:"grid",gridTemplateColumns:compactListsLayout?"1fr":"minmax(180px,1fr) minmax(120px,.6fr)",gap:8,alignItems:"center"}}>
                          <span style={{fontSize:13,color:T.text,fontWeight:600}}>{normalizeCostCenterCodeEntry(item).name}</span>
                          <span style={{fontSize:12,color:T.muted,fontWeight:700}}>{normalizeCostCenterCodeEntry(item).code}</span>
                        </div>
                      ) : isStockUnitsList ? (
                        <div style={{flex:1,display:"grid",gridTemplateColumns:"minmax(180px,1.2fr) minmax(120px,.7fr) minmax(120px,.7fr)",gap:8,alignItems:"center"}}>
                          <span style={{fontSize:13,color:T.text,fontWeight:600}}>{stockUnitItem.label}</span>
                          <span style={{fontSize:12,color:T.muted}}>Qtd padrão: {stockUnitItem.defaultQty || "—"}</span>
                          <span style={{fontSize:12,color:T.muted}}>Ref.: {stockUnitItem.refUnit || "—"}</span>
                        </div>
                      ) : isHarasCoatsList ? (
                        <div style={{flex:1,display:"flex",alignItems:"center",gap:10}}>
                          <div style={{width:18,height:18,borderRadius:4,background:normalizeHarasCoatEntry(item).hex,border:`1px solid ${T.border}`}}/>
                          <span style={{fontSize:13,color:T.text}}>{normalizeHarasCoatEntry(item).name}</span>
                          <span style={{fontSize:11,color:T.muted}}>{normalizeHarasCoatEntry(item).hex}</span>
                        </div>
                      ) : isBovineWeightRulesList ? (
                        <div style={{flex:1,display:"grid",gridTemplateColumns:bovineWeightRulesMobileLayout?"1fr":"minmax(160px,1.1fr) minmax(80px,.55fr) minmax(80px,.55fr) minmax(90px,.5fr)",gap:8,alignItems:"center"}}>
                          <span style={{fontSize:13,color:T.text,fontWeight:600}}>{normalizeBovineWeightRuleEntry(item).label}</span>
                          <div style={bovineWeightRulesMobileLayout?{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}:{display:"contents"}}>
                            <span style={{fontSize:12,color:T.muted}}>mín.: {normalizeBovineWeightRuleEntry(item).minAt}@</span>
                            <span style={{fontSize:12,color:T.muted}}>máx.: {normalizeBovineWeightRuleEntry(item).maxAt}@</span>
                          </div>
                          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}><div style={{width:16,height:16,borderRadius:4,background:normalizeBovineWeightRuleEntry(item).color,border:`1px solid ${T.border}`}}/><span style={{fontSize:11,color:T.muted,wordBreak:"break-all"}}>{normalizeBovineWeightRuleEntry(item).color}</span></div>
                        </div>
                      ) : isFinChartAccountsList ? (
                        <div style={{flex:1,display:"grid",gridTemplateColumns:financeAdminListLayout?"1fr":"minmax(110px,.7fr) minmax(220px,1.4fr) minmax(110px,.7fr) minmax(120px,.8fr) minmax(90px,.6fr)",gap:8,alignItems:"center"}}>
                          {financeAdminListLayout ? (
                          <div style={{display:"flex",flexDirection:"column",gap:4}}>
                            <span style={{fontSize:13,color:T.text,fontWeight:700}}>{financialAccountItem.code} • {financialAccountItem.name}</span>
                            <span style={{fontSize:12,color:T.muted}}>Tipo: {financialAccountItem.type}</span>
                            <span style={{fontSize:12,color:T.muted}}>Centro: {financialAccountItem.costCenter}</span>
                            <span style={{fontSize:11,color:financialAccountItem.taxable?T.accent:T.muted,fontWeight:700}}>{financialAccountItem.taxable?"Tributável":"Não tributável"}</span>
                          </div>
                        ) : (
                          <>
                            <span style={{fontSize:12,color:T.muted,fontWeight:700}}>{financialAccountItem.code}</span>
                            <span style={{fontSize:13,color:T.text,fontWeight:600}}>{financialAccountItem.name}</span>
                            <span style={{fontSize:12,color:T.muted}}>{financialAccountItem.type}</span>
                            <span style={{fontSize:12,color:T.muted}}>{financialAccountItem.costCenter}</span>
                            <span style={{fontSize:11,color:financialAccountItem.taxable?T.accent:T.muted,fontWeight:700}}>{financialAccountItem.taxable?"Tributável":"Não trib."}</span>
                          </>
                        )}
                        </div>
                      ) : isPayrollChargesList ? (
                        <div style={{flex:1,display:"grid",gridTemplateColumns:financeAdminListLayout?"1fr":"minmax(220px,1.2fr) minmax(90px,.55fr) minmax(220px,1fr)",gap:8,alignItems:"center"}}>
                          {financeAdminListLayout ? (
                          <div style={{display:"flex",flexDirection:"column",gap:4}}>
                            <span style={{fontSize:13,color:T.text,fontWeight:700}}>{payrollChargeItem.name}</span>
                            <span style={{fontSize:12,color:T.muted}}>Percentual: {payrollChargeItem.pct}%</span>
                            <span style={{fontSize:12,color:T.muted}}>{payrollChargeItem.notes || "Sem observação"}</span>
                          </div>
                        ) : (
                          <>
                            <span style={{fontSize:13,color:T.text,fontWeight:600}}>{payrollChargeItem.name}</span>
                            <span style={{fontSize:12,color:T.muted}}>{payrollChargeItem.pct}%</span>
                            <span style={{fontSize:12,color:T.muted}}>{payrollChargeItem.notes || "—"}</span>
                          </>
                        )}
                        </div>
                      ) : isTaxRegimesList ? (
                        <div style={{flex:1,display:"grid",gridTemplateColumns:financeAdminListLayout?"1fr":"minmax(170px,.9fr) minmax(260px,1.2fr) minmax(220px,1fr) minmax(90px,.6fr)",gap:8,alignItems:"center"}}>
                          {financeAdminListLayout ? (
                          <div style={{display:"flex",flexDirection:"column",gap:4}}>
                            <span style={{fontSize:13,color:T.text,fontWeight:700}}>{taxRegimeItem.name}</span>
                            <span style={{fontSize:12,color:T.muted}}>Tributos: {(taxRegimeItem.taxes || []).join(", ") || "—"}</span>
                            <span style={{fontSize:12,color:T.muted}}>{taxRegimeItem.notes || "Sem observação"}</span>
                            <span style={{fontSize:11,color:taxRegimeItem.active?T.green:T.muted,fontWeight:700}}>{taxRegimeItem.active?"Ativo":"Inativo"}</span>
                          </div>
                        ) : (
                          <>
                            <span style={{fontSize:13,color:T.text,fontWeight:600}}>{taxRegimeItem.name}</span>
                            <span style={{fontSize:12,color:T.muted}}>{(taxRegimeItem.taxes || []).join(", ") || "—"}</span>
                            <span style={{fontSize:12,color:T.muted}}>{taxRegimeItem.notes || "—"}</span>
                            <span style={{fontSize:11,color:taxRegimeItem.active?T.green:T.muted,fontWeight:700}}>{taxRegimeItem.active?"Ativo":"Inativo"}</span>
                          </>
                        )}
                        </div>
                      ) : <span style={{flex:1,fontSize:13,color:T.text}}>{item}</span>}
                      <div style={{display:"flex",gap:4}}>
                        <button onClick={()=>startEdit(idx)}
                          style={{background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:13,padding:"2px 6px"}}>✏️</button>
                        <button onClick={()=>removeItem(idx)}
                          style={{background:"none",border:"none",color:T.red,cursor:"pointer",fontSize:16,padding:"2px 6px",lineHeight:1}}>×</button>
                      </div>
                    </>
                  )}
                </div>
              )})}
              {(selectedList||[]).length===0&&(
                <div style={{color:T.muted,textAlign:"center",padding:24,fontSize:13}}>
                  Lista vazia. Adicione itens acima.
                </div>
              )}
            </div>
          </div>
        );

  return(
    <div>
      <Toast toast={toast}/>
      <div style={{fontWeight:700,color:T.text,fontSize:16,marginBottom:4}}>📋 Gerenciar Listas Suspensas</div>
      <div style={{fontSize:12,color:T.muted,marginBottom:16}}>
        Personalize as opções que aparecem nos campos de seleção de todos os módulos.
      </div>

      {/* Module filter */}
      <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:16}}>
        {modules.map(m=>(
          <button key={m} onClick={()=>{setFilterModule(m);setSelectedKey(null);}}
            style={{padding:"5px 12px",borderRadius:20,border:`1px solid ${filterModule===m?T.accent:T.border}`,
              background:filterModule===m?`${T.accent}18`:"transparent",
              color:filterModule===m?T.accent:T.muted,cursor:"pointer",fontSize:12,fontWeight:filterModule===m?700:400}}>
            {m}
          </button>
        ))}
      </div>

      {(!selectedKey && (filterModule === "Todos" || filterModule === "📦 Estoque")) && (
        <div style={{marginBottom:12,padding:"12px 14px",borderRadius:14,border:`1px solid ${T.border}`,background:T.card2,display:"grid",gap:10}}>
          <div style={{fontSize:13,fontWeight:700,color:T.text}}>Atalhos rápidos do Catálogo / Estoque</div>
          <div style={{fontSize:12,color:T.muted}}>Use estes atalhos para abrir rapidamente listas como <strong style={{color:T.text}}>Marcas de Produtos</strong>, tipos, categorias, subcategorias e unidades do catálogo.</div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {quickStockKeys.map((key)=><button key={key} onClick={()=>{ setFilterModule("📦 Estoque"); setSelectedKey(key); }} style={{padding:"7px 10px",borderRadius:999,border:`1px solid ${T.border}`,background:T.card,color:T.text,cursor:"pointer",fontSize:12,fontWeight:700}}>{SYSTEM_LISTS_LABELS[key]?.label || key}</button>)}
          </div>
        </div>
      )}
      {selectedKey && <div style={{marginBottom:12,padding:"10px 12px",borderRadius:12,border:`1px solid ${T.accent}44`,background:`${T.accent}14`,color:T.text,fontSize:12,fontWeight:700}}>Modo focado ativo: somente a lista em edição permanece visível até fechar o detalhe ou concluir a ação.</div>}
      <div style={{display:"grid",gridTemplateColumns:(selectedKey && !compactListsLayout)?"320px minmax(0,1fr)":"1fr",gap:16,alignItems:"start"}}>
        {/* List selector */}
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          {listsFocused.map(key=>{
            const meta = SYSTEM_LISTS_LABELS[key];
            const count = (lists[key]||[]).length;
            const isDefault = JSON.stringify(lists[key]||[]) === JSON.stringify(SYSTEM_LISTS_DEFAULTS[key]||[]);
            const isSelected = selectedKey===key;
            return(
              <React.Fragment key={key}>
                <div onClick={()=>setSelectedKey(isSelected?null:key)}
                  style={{padding:"10px 14px",borderRadius:10,cursor:"pointer",transition:"all .15s",
                    background:isSelected?`${T.accent}18`:T.card2,
                    border:`1px solid ${isSelected?T.accent:T.border}`}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <span style={{fontSize:16}}>{meta.icon}</span>
                      <div>
                        <div style={{fontSize:13,fontWeight:600,color:T.text}}>{meta.label}</div>
                        <div style={{fontSize:10,color:T.muted}}>{meta.module}</div>
                      </div>
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                      {!isDefault&&<span style={{fontSize:9,color:T.accent,fontWeight:700,background:`${T.accent}22`,
                        padding:"2px 6px",borderRadius:10}}>EDIT.</span>}
                      <span style={{fontSize:11,color:T.muted}}>{count}</span>
                      <span style={{fontSize:12,color:isSelected?T.accent:T.muted}}>›</span>
                    </div>
                  </div>
                </div>
                {compactListsLayout && isSelected && listEditor}
              </React.Fragment>
            );
          })}
        </div>

        {/* List editor */}
        {!compactListsLayout && listEditor}
      </div>
    </div>
  );
}


export default SystemListsManager;
